/**
 * Prompt Library runtime (spec section 13).
 *
 * Wraps the shipped defaults in an editable, persistable collection.
 * User-authored prompts live in IndexedDB; defaults are merged in so a
 * fresh install always has something usable offline.
 */

import { PROMPTS, DEFAULT_NEGATIVE_PROMPT, clonePrompt } from '../data/prompts.js';
import { hashString } from '../utils/hash.js';

export function createPromptLibrary(initial = PROMPTS) {
  let items = initial.map(clonePrompt);

  const api = {
    all() {
      return items.slice();
    },
    get(id) {
      return items.find((p) => p.id === id) ?? null;
    },
    forScene(sceneId) {
      return items.filter((p) => p.sceneId === sceneId);
    },
    enabledForScene(sceneId) {
      return items.filter((p) => p.sceneId === sceneId && p.enabled);
    },
    forProvider(providerId, sceneId = null) {
      return items.filter(
        (p) =>
          p.enabled &&
          (sceneId === null || p.sceneId === sceneId) &&
          (p.providerCompatibility.includes(providerId) || p.providerCompatibility.includes('generic')),
      );
    },
    /** Best default entry for a scene: first enabled, else any, else null. */
    preferredForScene(sceneId, providerId = 'generic') {
      const candidates = api.forProvider(providerId, sceneId);
      if (candidates.length) return candidates[0];
      return api.forScene(sceneId)[0] ?? null;
    },
    add(entry) {
      const next = {
        id: entry.id ?? `user-${hashString(`${entry.sceneId}:${entry.prompt}`)}`,
        negativePrompt: DEFAULT_NEGATIVE_PROMPT,
        duration: 8,
        aspectRatio: '9:16',
        style: 'cinematic-realistic',
        providerCompatibility: ['google-veo', 'google-vids', 'generic'],
        enabled: true,
        tags: [],
        ...entry,
      };
      items = [next, ...items.filter((p) => p.id !== next.id)];
      return next;
    },
    update(id, patch) {
      const index = items.findIndex((p) => p.id === id);
      if (index === -1) return null;
      const next = { ...items[index], ...patch };
      items = items.map((p, i) => (i === index ? next : p));
      return next;
    },
    remove(id) {
      const before = items.length;
      items = items.filter((p) => p.id !== id);
      return items.length !== before;
    },
    /** Replace the whole collection (used when loading from storage). */
    replaceAll(list) {
      items = list.map(clonePrompt);
    },
    /** Merge user prompts on top of defaults, de-duplicated by id. */
    mergeUserPrompts(userPrompts) {
      const byId = new Map(items.map((p) => [p.id, p]));
      for (const p of userPrompts) byId.set(p.id, { ...p, userCreated: true });
      items = Array.from(byId.values());
    },
    size() {
      return items.length;
    },
  };

  return api;
}

/** Integrity helper used by the test suite. */
export function validatePromptEntries(entries) {
  const issues = [];
  const seen = new Set();
  for (const entry of entries) {
    for (const field of ['id', 'sceneId', 'prompt', 'duration', 'aspectRatio', 'style']) {
      if (entry[field] === undefined || entry[field] === null || entry[field] === '') {
        issues.push(`${entry.id ?? '<no id>'}: missing ${field}`);
      }
    }
    if (seen.has(entry.id)) issues.push(`duplicate prompt id: ${entry.id}`);
    seen.add(entry.id);
    if (typeof entry.prompt === 'string' && entry.prompt.length < 40) {
      issues.push(`${entry.id}: prompt body looks too short to be useful`);
    }
  }
  return issues;
}
